import {
	BASE_PRECISION_EXP,
	BigNum,
	PRICE_PRECISION_EXP,
	ZERO,
} from '@velocity-exchange/sdk';
import {
	UIOrderType,
	calculateLiquidationPriceAfterPerpTrade,
} from '@velocity-exchange/common';
import { useMemo } from 'react';
import useVelocityClient from 'src/hooks/useVelocityClient';
import useGetOraclePriceForMarket from 'src/hooks/useGetOraclePriceForMarket';
import useInfoForCurrentlySelectedMarket from 'src/hooks/useInfoForCurrentlySelectedMarket';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import useVelocityAccountStore from 'src/stores/useVelocityAccountsStore';
import useGetDisplayLeverageForMarket from 'src/hooks/useGetDisplayLeverageForMarket';
import { useCurrentAccountPositions } from 'src/hooks/query/accountTradingData/usePositions';
import { useEffectiveMarginMode } from 'src/hooks/marginMode/useEffectiveMarginMode';
import { parseInputBigNumOrZero } from 'src/utils/parseInputBigNum';

export default function useTradeFormEstimatedLiquidationPrice() {
	const velocityClient = useVelocityClient();
	const selectedMarketId = useVelocityStore(
		(s) => s.selectedMarket.current?.marketId
	);
	const selectedMarketIndex = selectedMarketId?.marketIndex;

	const {
		baseSizeStringValue,
		priceBoxStringValue,
		secondaryPriceBoxStringValue,
		side,
		orderType,
		priceImpact: { entryPrice } = { entryPrice: ZERO },
	} = useVelocityStore((s) => s.tradeForm);

	const openPerpPositions = useCurrentAccountPositions();
	const currentPosition =
		openPerpPositions.find((pos) => pos.marketIndex === selectedMarketIndex) ??
		null;
	const currentPositionDirection = currentPosition?.direction;
	const currentUserClient = useVelocityAccountStore(
		(s) => s.getCurrentUserAccount()?.client
	);

	// Get effective margin mode (checks position first, then settings)
	const { mode: effectiveMarginMode } = useEffectiveMarginMode(
		selectedMarketId?.key
	);

	// Determine margin type for liquidation price calculation
	const marginType = useMemo(() => {
		if (!currentUserClient || selectedMarketIndex === undefined) {
			return undefined;
		}

		// If there's an existing position, check if it's isolated
		if (currentPosition) {
			const sdkPosition =
				currentUserClient.getPerpPosition(selectedMarketIndex);
			if (
				sdkPosition &&
				currentUserClient.isPerpPositionIsolated(sdkPosition)
			) {
				return 'Isolated' as const;
			}
			return 'Cross' as const;
		}

		// No position exists - use the effective margin mode setting
		return effectiveMarginMode === 'isolated'
			? ('Isolated' as const)
			: ('Cross' as const);
	}, [
		currentUserClient,
		selectedMarketIndex,
		currentPosition,
		effectiveMarginMode,
	]);

	const getOraclePriceForMarket = useGetOraclePriceForMarket();
	const oraclePrice = getOraclePriceForMarket(selectedMarketId);

	const displayDecimals =
		useInfoForCurrentlySelectedMarket()?.info?.genericInfo
			?.priceDisplayDecimals;

	// Calculate display leverage for getBitFlags
	const displayLeverage = useGetDisplayLeverageForMarket(
		selectedMarketIndex ?? 0
	);

	return useMemo(() => {
		if (
			!oraclePrice ||
			!currentUserClient ||
			selectedMarketIndex === undefined ||
			// Match the order types allowed in `calculateLiquidationPriceAfterPerpTrade`.
			![
				'limit',
				'market',
				'oracle',
				'stopMarket',
				'stopLimit',
				'oracleLimit',
			].includes(orderType)
		) {
			return 0;
		}

		const baseSize = parseInputBigNumOrZero(
			baseSizeStringValue ? baseSizeStringValue : '0',
			BASE_PRECISION_EXP
		);

		const limitPrice = (() => {
			switch (orderType) {
				case 'oracleLimit':
					return BigNum.from(entryPrice, PRICE_PRECISION_EXP).add(
						parseInputBigNumOrZero(priceBoxStringValue, PRICE_PRECISION_EXP)
					);
				case 'stopLimit':
					return parseInputBigNumOrZero(
						secondaryPriceBoxStringValue,
						PRICE_PRECISION_EXP
					);
				case 'limit':
				case 'stopMarket':
					return parseInputBigNumOrZero(
						priceBoxStringValue,
						PRICE_PRECISION_EXP
					);
				default:
					return BigNum.zero();
			}
		})();

		const isReducingPosition =
			currentPositionDirection &&
			((currentPositionDirection === 'short' && side === 'buy') ||
				(currentPositionDirection === 'long' && side === 'sell'));

		return calculateLiquidationPriceAfterPerpTrade({
			estEntryPrice: entryPrice,
			orderType: orderType as UIOrderType,
			perpMarketIndex: selectedMarketIndex,
			tradeBaseSize: baseSize.val,
			oraclePrice: oraclePrice.val,
			isLong: side === 'buy',
			userClient: currentUserClient,
			limitPrice: limitPrice.val,
			precision: displayDecimals,
			capLiqPrice: !isReducingPosition, // don't cap if reducing a position
			marginType,
		});
	}, [
		baseSizeStringValue,
		priceBoxStringValue,
		entryPrice,
		orderType,
		selectedMarketIndex,
		side,
		displayLeverage,
		oraclePrice,
		currentPositionDirection,
		displayDecimals,
		velocityClient,
		currentUserClient,
		marginType,
	]);
}
